//! Product Tour:
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "./driverTourTheme.css";

const ACTIVE_CLASS = "ig-tour-active";

let activeCleanupInterval = null;

const getResumeTourSteps = () => [
  {
    element: "#CustomRadioGroup",
    popover: {
      title: "Choose Your Path",
      description: "Select one of the available options to proceed. Each choice updates the selection instantly.",
      side: "bottom",
      align: "center",
    },
  },
  {
    element: "#UploadResume",
    popover: {
      title: "Upload Your Resume",
      description:
        "Drag and drop your resume or browse to upload a PDF/DOCX. The system will extract your data automatically.",
      side: "right",
      align: "center",
    },
  },
  {
    element: "#FormBuilder",
    popover: {
      title: "Build Your Resume",
      description:
        "Manually fill in your personal info, work experience, education, and skills. You can add multiple entries for each section.",
      side: "right",
      align: "center",
    },
  },
  {
    element: "#CountrySelection",
    popover: {
      title: "Select Target Country",
      description:
        "Choose the country where you'll be applying. This ensures your resume meets local formatting standards.",
      side: "right",
      align: "center",
    },
  },
  {
    element: "#AdditionalFields",
    popover: {
      title: "Country-Specific Fields",
      description:
        "Provide any additional information required for the selected country, like a professional photo or date of birth.",
      side: "right",
      align: "center",
    },
  },
  {
    element: "#SmartAdjustment",
    popover: {
      title: "Smart Form Adjustment",
      description:
        "The system will suggest hiding or completing fields to optimize your resume for the selected country's standards.",
      side: "right",
      align: "center",
    },
  },
  {
    element: "#ReviewGenerate",
    popover: {
      title: "Review & Generate",
      description:
        "Check your resume completion, review the selected template, and generate a polished final document.",
      side: "top",
      align: "center",
    },
  },
];

const normalizeTourOptions = (arg) => {
  if (typeof arg === "boolean") return { onlyIfFirstVisit: arg };
  if (arg && typeof arg === "object") return arg;
  return {};
};

const isElementDisplayed = (el) => {
  if (!el) return false;
  const style = window.getComputedStyle(el);
  if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

const isElementInViewport = (el, margin = 10) => {
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const vw = window.innerWidth || document.documentElement.clientWidth;
  return rect.bottom >= margin && rect.right >= margin && rect.top <= vh - margin && rect.left <= vw - margin;
};

// Collect steps whose elements exist + are displayed for the current screen.
// (Scrolling is handled per-step so we don't drop below-the-fold sections.)
const waitForAvailableSteps = async (steps, timeoutMs = 5000, intervalMs = 150) => {
  const start = Date.now();
  return new Promise((resolve) => {
    const tick = () => {
      const availableSteps = steps.filter((s) => {
        if (!s?.element) return false;
        const el = document.querySelector(s.element);
        return isElementDisplayed(el);
      });

      if (availableSteps.length) return resolve(availableSteps);
      if (Date.now() - start >= timeoutMs) return resolve([]);

      window.setTimeout(tick, intervalMs);
    };

    tick();
  });
};

const startResumeTourInternal = (rawOptions) => {
  const options = normalizeTourOptions(rawOptions);
  const { onlyIfFirstVisit = false, storageKey = "tour_resume_done" } = options;

  if (onlyIfFirstVisit && localStorage.getItem(storageKey)) return;

  if (activeCleanupInterval) clearInterval(activeCleanupInterval);

  document.documentElement.classList.add(ACTIVE_CLASS);

  (async () => {
    const rawSteps = getResumeTourSteps();
    const steps = await waitForAvailableSteps(rawSteps);

    if (!steps.length) {
      document.documentElement.classList.remove(ACTIVE_CLASS);
      return;
    }

    const resumeBuilderObj = driver({
      showProgress: true,
      overlayOpacity: 0.62,
      allowClose: true,
      smoothScroll: true,
      stagePadding: 10,
      stageRadius: 14,
      popoverOffset: 12,
      popoverClass: "ig-driver-popover",
      nextBtnText: "Next",
      prevBtnText: "Back",
      doneBtnText: "Finish",
      onPopoverRender: (popover) => {
        if (popover?.wrapper) {
          // Guarantees our theme is applied on step 1 too.
          popover.wrapper.classList.add("ig-driver-popover");
          popover.wrapper.style.zIndex = "100002";
          popover.wrapper.style.opacity = "1";
          popover.wrapper.style.visibility = "visible";
        }
      },
      onHighlightStarted: (element) => {
        if (element && !isElementInViewport(element, 16)) {
          try {
            element.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
          } catch (e) {}
        }
      },
      onDestroyed: () => document.documentElement.classList.remove(ACTIVE_CLASS),
      steps,
    });

    resumeBuilderObj.drive();
  })();

  activeCleanupInterval = window.setInterval(() => {
    const overlayExists = Boolean(document.querySelector(".driver-overlay"));
    if (!overlayExists) {
      clearInterval(activeCleanupInterval);
      activeCleanupInterval = null;
      document.documentElement.classList.remove(ACTIVE_CLASS);
      localStorage.setItem(storageKey, "true");
    }
  }, 350);
};

export const resumeBuilderTour = () => startResumeTourInternal({ onlyIfFirstVisit: false });
export const resumeBuilderTourIfFirstVisit = () =>
  startResumeTourInternal({ onlyIfFirstVisit: true, storageKey: "tour_resume_done" });